/**
 * Manual Review Queue — full operator page (Phase H).
 *
 * Consumes:
 *   GET  /api/manual-review/queue       — filterable, paginated queue
 *   GET  /api/manual-review/counts      — dashboard-badge counters
 *   POST /api/manual-review/assign      — assign to a user
 *   POST /api/manual-review/note        — append a review note
 *   POST /api/manual-review/resolve     — mark item resolved (append-only)
 *
 * Design contract (Phase H PHI safety):
 *   - Rows show IDs, states, timestamps, and control numbers only.
 *   - No patient name / member id / claim body ever renders in a row.
 *   - The drawer only surfaces metadata already returned by the queue
 *     endpoint. Nothing extra is fetched.
 *   - Safe user-facing errors: HTTP body detail is normalised by the
 *     axios interceptor; if that fails we show a generic message.
 *
 * Access: any user whose backend role passes ``_require_review_role``.
 *   - AZAI staff (super_admin / billing_manager) with elevation.
 *   - Agency Admin / Supervisor / BillingLead / Integration.
 *   Row filtering is enforced by the backend against the current
 *   agency filter — the UI never bypasses.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import {
  Warning,
  XCircle,
  Clock,
  Bell,
  ArrowClockwise,
  CaretLeft,
  CaretRight,
  UserCircle,
  ChatCircleText,
  CheckCircle,
} from '@phosphor-icons/react';

const REVIEW_ROLES = new Set(['Admin', 'Supervisor', 'BillingLead', 'Integration']);

const isReviewCapable = (user) =>
  user?.entity_type === 'azai' || REVIEW_ROLES.has(user?.role);

const KIND_LABEL = {
  watchdog_fired: 'Watchdog fired',
  ack_unmatched: 'Unmatched ack',
  submission_stalled: 'Stalled submission',
  alert_open: 'Open alert',
};

const KIND_ICON = {
  watchdog_fired: Clock,
  ack_unmatched: XCircle,
  submission_stalled: Warning,
  alert_open: Bell,
};

const SEVERITY_BADGE = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-amber-100 text-amber-800',
  medium: 'bg-blue-100 text-blue-800',
  low: 'bg-slate-100 text-slate-700',
};

const STATE_BADGE = {
  timed_out: 'bg-amber-100 text-amber-800',
  timeout: 'bg-amber-100 text-amber-800',
  rejected: 'bg-red-100 text-red-800',
  manual_review: 'bg-red-100 text-red-800',
  unknown: 'bg-slate-100 text-slate-700',
  malformed: 'bg-slate-100 text-slate-700',
  new: 'bg-blue-100 text-blue-800',
};

const PAGE_SIZE = 25;

const KIND_OPTIONS = [
  { value: 'all', label: 'All kinds' },
  { value: 'watchdog_fired', label: 'Watchdogs fired' },
  { value: 'ack_unmatched', label: 'Unmatched acks' },
  { value: 'submission_stalled', label: 'Stalled submissions' },
  { value: 'alert_open', label: 'Open alerts' },
];

const SEVERITY_OPTIONS = [
  { value: 'all', label: 'All severities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const RESPONSE_OPTIONS = [
  { value: 'all', label: 'All response types' },
  { value: 'TA1', label: 'TA1' },
  { value: '999', label: '999' },
  { value: '277CA', label: '277CA' },
  { value: 'adjudication', label: 'Adjudication (835)' },
];

const buildKey = (item) => `${item.kind}:${item.id}`;

const humanizeError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.length < 200) return detail;
  if (err?.response?.status === 403) return 'Not authorized for this action.';
  if (err?.response?.status === 404) return 'Item not found in the current tenant.';
  return 'Action failed. Please retry.';
};

const ManualReviewPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [kindFilter, setKindFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [responseFilter, setResponseFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const canReview = isReviewCapable(user);

  const fetchQueue = useCallback(async () => {
    if (!canReview) return;
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (severityFilter !== 'all') params.severity = severityFilter;
      if (responseFilter !== 'all') params.response_type = responseFilter;
      const { data } = await api.get('/manual-review/queue', { params });
      let rows = data.items || [];
      if (kindFilter !== 'all') rows = rows.filter((r) => r.kind === kindFilter);
      setItems(rows);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(humanizeError(e));
    } finally {
      setLoading(false);
    }
  }, [canReview, page, severityFilter, responseFilter, kindFilter]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue, refreshTick]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  useEffect(() => {
    if (clampedPage !== page) setPage(clampedPage);
  }, [clampedPage, page]);

  const grouped = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        acc.total += 1;
        acc[item.kind] = (acc[item.kind] || 0) + 1;
        return acc;
      },
      { total: 0 },
    );
  }, [items]);

  if (!canReview) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-16 text-center" data-testid="mrq-forbidden">
          <Warning size={48} className="mx-auto text-amber-500 mb-4" weight="duotone" />
          <h1 className="text-xl font-semibold text-slate-900">
            Manual Review Queue is restricted
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Your role does not include access to the review queue. Ask an admin
            for the Supervisor, BillingLead or Integration role, or contact AZAI
            support.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="manual-review-page">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Manual Review Queue
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Fired watchdogs, unmatched acknowledgments, and stalled
              submissions that need operator attention. Row content shows
              metadata only — no PHI.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRefreshTick((t) => t + 1)}
              disabled={loading}
              data-testid="mrq-refresh-btn"
            >
              <ArrowClockwise size={16} className="mr-1.5" />
              Refresh
            </Button>
          </div>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="mrq-summary-tiles">
          <SummaryTile label="Watchdogs" value={grouped.watchdog_fired || 0} icon={Clock} tone="high" testId="tile-watchdog" />
          <SummaryTile label="Unmatched acks" value={grouped.ack_unmatched || 0} icon={XCircle} tone="high" testId="tile-ack" />
          <SummaryTile label="Stalled" value={grouped.submission_stalled || 0} icon={Warning} tone="critical" testId="tile-stalled" />
          <SummaryTile label="Open alerts" value={grouped.alert_open || 0} icon={Bell} tone="medium" testId="tile-alert" />
          <SummaryTile label="Total (page)" value={grouped.total} icon={CheckCircle} tone="medium" testId="tile-total" />
        </div>

        <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg p-3" data-testid="mrq-filters">
          <FilterSelect label="Kind" value={kindFilter} onChange={setKindFilter} options={KIND_OPTIONS} testId="filter-kind" />
          <FilterSelect label="Severity" value={severityFilter} onChange={setSeverityFilter} options={SEVERITY_OPTIONS} testId="filter-severity" />
          <FilterSelect label="Response" value={responseFilter} onChange={setResponseFilter} options={RESPONSE_OPTIONS} testId="filter-response" />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="mrq-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2">Kind</th>
                <th className="text-left px-4 py-2">Reference</th>
                <th className="text-left px-4 py-2">State</th>
                <th className="text-left px-4 py-2">Severity</th>
                <th className="text-left px-4 py-2">Deadline / At</th>
                <th className="text-left px-4 py-2">Assigned</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="mrq-empty">
                    Queue is clean. No items require review.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-400" data-testid="mrq-loading">
                    Loading queue…
                  </td>
                </tr>
              )}
              {items.map((item) => {
                const KindIcon = KIND_ICON[item.kind] || Warning;
                return (
                  <tr
                    key={buildKey(item)}
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelected(item)}
                    data-testid={`mrq-row-${item.id}`}
                  >
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2 text-slate-700">
                        <KindIcon size={16} className="text-slate-500" />
                        <span className="text-xs">{KIND_LABEL[item.kind] || item.kind}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">
                      {item.submission_id || item.id}
                      {item.response_type && (
                        <Badge className="ml-2 bg-slate-100 text-slate-700" data-testid={`mrq-response-${item.id}`}>
                          {item.response_type}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Badge className={STATE_BADGE[item.state] || 'bg-slate-100 text-slate-700'}>
                        {item.state}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      <Badge className={SEVERITY_BADGE[item.severity] || 'bg-slate-100 text-slate-700'}>
                        {item.severity}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {formatDateTime(item.deadline || item.fired_at || item.received_at || item.age_seconds) || '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">
                      {item.assigned_to ? (
                        <div className="flex items-center gap-1">
                          <UserCircle size={14} />
                          <span>{item.assigned_to.slice(0, 8)}</span>
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(item);
                        }}
                        data-testid={`mrq-open-drawer-${item.id}`}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm">
            <span className="text-slate-500" data-testid="mrq-pagination">
              Page {page} of {totalPages} — {total} total items
            </span>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="mrq-prev-btn"
              >
                <CaretLeft size={14} />
                Prev
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="mrq-next-btn"
              >
                Next
                <CaretRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ReviewDrawer
        item={selected}
        currentUser={user}
        onClose={() => setSelected(null)}
        onMutated={() => setRefreshTick((t) => t + 1)}
      />
    </Layout>
  );
};

const SummaryTile = ({ label, value, icon: Icon, tone, testId }) => {
  const toneClasses = {
    critical: value > 0 ? 'border-red-200 bg-red-50 text-red-900' : 'border-slate-200 bg-white text-slate-500',
    high: value > 0 ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-500',
    medium: value > 0 ? 'border-blue-200 bg-blue-50 text-blue-900' : 'border-slate-200 bg-white text-slate-500',
  };
  return (
    <div className={`border rounded-lg p-3 ${toneClasses[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-medium">
        <Icon size={14} weight={value > 0 ? 'fill' : 'regular'} />
        <span>{label}</span>
      </div>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs font-medium text-slate-500">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-40 text-xs" data-testid={testId}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const ReviewDrawer = ({ item, currentUser, onClose, onMutated }) => {
  const [note, setNote] = useState('');
  const [resolveNote, setResolveNote] = useState('');
  const [resolution, setResolution] = useState('resolved_manually');
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNote('');
    setResolveNote('');
    setResolution('resolved_manually');
    setAssignee('');
  }, [item?.id]);

  if (!item) return null;

  const supportsAssign = ['watchdog_fired', 'submission_stalled'].includes(item.kind);
  const supportsNote = supportsAssign;
  const supportsResolve = ['watchdog_fired', 'submission_stalled', 'alert_open'].includes(item.kind);

  const doAssign = async (target) => {
    if (!target || target.length < 1) {
      toast.error('Assignee cannot be blank.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/manual-review/assign', {
        kind: item.kind,
        item_id: item.id,
        assigned_to: target,
      });
      toast.success('Assigned.');
      onMutated?.();
      onClose?.();
    } catch (e) {
      toast.error(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  const doNote = async () => {
    if (note.trim().length === 0) {
      toast.error('Note cannot be empty.');
      return;
    }
    setBusy(true);
    try {
      await api.post('/manual-review/note', {
        kind: item.kind,
        item_id: item.id,
        note: note.trim(),
      });
      toast.success('Note appended.');
      setNote('');
      onMutated?.();
    } catch (e) {
      toast.error(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  const doResolve = async () => {
    if (resolveNote.trim().length < 3 || resolution.trim().length < 3) {
      toast.error('Both resolution and note are required (min 3 chars).');
      return;
    }
    setBusy(true);
    try {
      await api.post('/manual-review/resolve', {
        kind: item.kind,
        item_id: item.id,
        resolution: resolution.trim(),
        note: resolveNote.trim(),
      });
      toast.success('Item marked resolved (append-only history preserved).');
      onMutated?.();
      onClose?.();
    } catch (e) {
      toast.error(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={!!item} onOpenChange={(open) => (!open && onClose())}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="mrq-drawer">
        <SheetHeader>
          <SheetTitle>{KIND_LABEL[item.kind] || item.kind}</SheetTitle>
          <SheetDescription>
            Metadata only — this drawer never renders patient or member
            identifiers. To view the underlying submission body, use the
            submissions detail page.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4 text-sm">
          <MetaBlock label="Item id" value={item.id} mono />
          {item.submission_id && <MetaBlock label="Submission id" value={item.submission_id} mono />}
          {item.batch_id && <MetaBlock label="Batch id" value={item.batch_id} mono />}
          {item.filename && <MetaBlock label="Filename" value={item.filename} mono />}
          {item.interchange_control_number && (
            <MetaBlock label="ISA control number" value={item.interchange_control_number} mono />
          )}
          {item.artifact_id && <MetaBlock label="Artifact id" value={item.artifact_id} mono />}
          {item.response_type && <MetaBlock label="Response type" value={item.response_type} />}
          <MetaBlock label="State" value={item.state} />
          <MetaBlock label="Severity" value={item.severity} />
          {item.deadline && <MetaBlock label="Deadline" value={formatDateTime(item.deadline)} />}
          {item.fired_at && <MetaBlock label="Fired at" value={formatDateTime(item.fired_at)} />}
          {item.received_at && <MetaBlock label="Received at" value={formatDateTime(item.received_at)} />}
          {item.assigned_to && <MetaBlock label="Assigned to" value={item.assigned_to} mono />}
          {item.resolved_at && <MetaBlock label="Resolved at" value={formatDateTime(item.resolved_at)} />}
          {item.recommended_action && <MetaBlock label="Recommended action" value={item.recommended_action} />}

          {Array.isArray(item.review_notes) && item.review_notes.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                <ChatCircleText size={12} />
                Review notes
              </p>
              <ul className="space-y-2 border border-slate-200 rounded-md p-2 bg-slate-50">
                {item.review_notes.map((n) => (
                  <li key={n.id} className="text-xs text-slate-700">
                    <div className="text-slate-500">{n.actor_email || n.actor_id} — {formatDateTime(n.created_at)}</div>
                    <div>{n.text}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {supportsAssign && (
            <div className="border-t border-slate-100 pt-4 space-y-2" data-testid="mrq-assign-section">
              <label className="text-xs font-medium text-slate-600">Assign to (user id / email)</label>
              <div className="flex items-center gap-2">
                <Input
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  placeholder="user-id-or-email"
                  data-testid="mrq-assign-input"
                />
                <Button
                  size="sm"
                  onClick={() => doAssign(assignee.trim())}
                  disabled={busy}
                  data-testid="mrq-assign-submit"
                >
                  Assign
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => doAssign(currentUser?.id || currentUser?.email || '')}
                  disabled={busy}
                  data-testid="mrq-assign-me"
                >
                  Assign to me
                </Button>
              </div>
            </div>
          )}

          {supportsNote && (
            <div className="border-t border-slate-100 pt-4 space-y-2" data-testid="mrq-note-section">
              <label className="text-xs font-medium text-slate-600">Add a review note</label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Context of what was checked / handed off / observed…"
                maxLength={1000}
                data-testid="mrq-note-input"
              />
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={doNote}
                  disabled={busy}
                  data-testid="mrq-note-submit"
                >
                  Append note
                </Button>
              </div>
            </div>
          )}

          {supportsResolve && (
            <div className="border-t border-slate-100 pt-4 space-y-2" data-testid="mrq-resolve-section">
              <label className="text-xs font-medium text-slate-600">Resolve (append-only)</label>
              <Input
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                placeholder="resolution code (e.g. resubmit_after_fix)"
                data-testid="mrq-resolve-code"
              />
              <Textarea
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                placeholder="Explain the resolution (at least 3 characters)…"
                data-testid="mrq-resolve-note"
              />
              <p className="text-xs text-slate-500">
                Resolutions append a permanent ledger row — the original
                event is never deleted. The audit chain preserves it.
              </p>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={doResolve}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="mrq-resolve-submit"
                >
                  Mark resolved
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const MetaBlock = ({ label, value, mono }) => (
  <div>
    <p className="text-xs font-medium text-slate-500">{label}</p>
    <p className={`text-sm text-slate-800 ${mono ? 'font-mono break-all' : ''}`}>
      {value ?? '—'}
    </p>
  </div>
);

export default ManualReviewPage;
